'use client';

import type React from 'react';

import { ConsentStatus } from '../domain/types';
import { useConsentContext } from './consent-context-provider';
import { useConsent } from './use-consent';

export interface ConsentGateProps {
  /**
   * Content to render when consent is granted
   */
  children: React.ReactNode;
  /**
   * Optional fallback to render when consent is not granted
   */
  fallback?: React.ReactNode;
}

/**
 * Component that conditionally renders children based on consent status
 *
 * Reads requiresConsent from ConsentContextProvider and checks if user
 * has accepted all cookies. Only renders children if:
 * - User doesn't require consent (not in strict consent state), OR
 * - User has explicitly accepted all cookies
 *
 * Used to gate non-essential tracking scripts and advertising features
 *
 * @param props - Component props
 * @param props.children - Content to render when consent is granted
 * @param props.fallback - Optional content to render when consent is not granted
 *
 * @example
 * ```typescript
 * <ConsentGate>
 *   <AnalyticsScripts />
 * </ConsentGate>
 * ```
 */
export function ConsentGate({ children, fallback = null }: ConsentGateProps) {
  const { requiresConsent } = useConsentContext();
  const { status } = useConsent();

  // If consent is not required, always render children
  if (!requiresConsent) {
    return <>{children}</>;
  }

  // Only render children if user has accepted all cookies
  if (status === ConsentStatus.ALL_COOKIES) {
    return <>{children}</>;
  }

  // Otherwise render fallback
  return <>{fallback}</>;
}
